import { getItemsAddress } from '@common/addressHelpers'
import { BLINDBOX_PRICE } from '@common/constans'
import {
  AutoRenewIcon,
  Button,
  Card,
  CardBody,
  Flex,
  ProfileAvatar,
  Text,
  useModal,
  useWalletModal,
} from '@library/uikit'
import { baseColors } from '@library/uikit/src/theme/colors'
import { useTranslation } from '@library/localization'
import useAuth from 'hooks/useAuth'
import { useBlindBox } from 'hooks/useContract'
import { useDagenApi } from 'hooks/useDagenApi'
import useToast from 'hooks/useToast'
import useWallet from 'hooks/useWallet'
import noop from 'lodash/noop'
import NextImage from 'next/image'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Image } from 'state/nftMarket/types'
import { useProfile } from 'state/profile/hooks'
import styled, { css } from 'styled-components'
import GoldChargeModal from 'views/Gens/Market/Profile/components/GoldChargeModal'
import BlindBoxInfoModal from './BlindBoxInfoModal'

export interface BlindBoxCardProps {
  boxId: string
  image: string
  openBlindBoxPrice?: string
  blindBoxIndex: number
  index: number
  buying: boolean
  mint: boolean
  setBuying: React.Dispatch<React.SetStateAction<boolean>>
  onSuccess?: (index: number, ids: number[]) => void
}

export const BlindContainer = styled(Flex)<{ disabled?: boolean }>`
  flex-direction: column;

  .title {
    color: ${({ disabled }) => (disabled ? baseColors.grey : 'white')};
    opacity: ${({ disabled }) => (disabled ? 1 : 0.5)};
    font-size: 16px;
    text-align: center;
    font-weight: 400;
  }

  &:hover .title {
    opacity: 1;
  }
`

export const ViewOnBlockChain = styled.div`
  right: 0;
  position: absolute;
  bottom: -32px;
  border: 4px white solid;
`

export const CollectionAvatar = styled(ProfileAvatar)`
  left: 0;
  position: absolute;
  top: -32px;
  border: 4px white solid;
`

const ImageCard = styled(Card)`
  padding: 8px;

  div {
    border-radius: 32px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 4px;

    div {
      border-radius: 12px;
    }
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 8px;

    div {
      border-radius: 32px;
    }
  }
`

const StyledHotCollectionCard = styled(Card)<{ theme; disabled?: boolean }>`
  border-radius: 32px;
  ${({ theme, disabled }) =>
    css`
      .box-pic {
        border: 4px solid ${disabled ? theme.colors.disabled : theme.colors.tertiary};

        ${({ theme }) => theme.mediaQueries.sm} {
          border-radius: 12px;
        }

        ${({ theme }) => theme.mediaQueries.md} {
          border-radius: 32px;
        }
      }

      .box-btn {
        background-color: ${disabled ? theme.colors.disabled : theme.colors.tertiary};
        height: 48px;
        font-size: 22px;
        font-weight: 600;

        ${({ theme }) => theme.mediaQueries.sm} {
          font-size: 16px;
          height: 32px;
        }

        ${({ theme }) => theme.mediaQueries.md} {
          font-size: 22px;
          height: 48px;
        }
      }
    `}

  ${({ theme, disabled }) =>
    disabled
      ? ''
      : css`
          &:hover {
            cursor: pointer;
          }

          &:hover .box-pic {
            border: 4px solid ${disabled ? theme.colors.disabled : theme.colors.primary};
          }

          &:hover .box-btn {
            background-color: ${disabled ? theme.colors.disabled : theme.colors.primary};
          }
        `}
`

const BlindBoxCard: React.FC<PropsWithChildren<BlindBoxCardProps>> = ({
  children,
  boxId,
  image,
  index,
  blindBoxIndex,
  buying,
  mint,
  setBuying,
  onSuccess = noop,
}) => {
  const { chainId, walletAddress } = useWallet()
  const { t } = useTranslation()
  const { toastError } = useToast()
  const [box, setBox] = useState<{ name: string; image: Image }>()
  const [onPresentGoldChargeModal] = useModal(<GoldChargeModal />)
  const [onPresentBlindBoxAlertModal] = useModal(
    <BlindBoxInfoModal onCharge={onPresentGoldChargeModal} onConfirm={purchase} />,
  )
  const { refresh } = useProfile()
  const [submiting, setSubmiting] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const dagenApi = useDagenApi()
  const router = useRouter()

  const { login, logout } = useAuth()
  const { onPresentConnectModal } = useWalletModal(login, logout, t)

  useEffect(() => {
    setBox({ name: `blind${index}`, image: { thumbnail: image, original: image } })
  }, [image, index])

  const presentDialog = async () => {
    if (walletAddress) {
      onPresentBlindBoxAlertModal()
    } else {
      onPresentConnectModal()
    }
  }

  async function purchase() {
    try {
      setSubmiting(true)
      setBuying(true)

      const collectionAddress = await getItemsAddress(chainId)
      const res = await dagenApi.post('openBlindBox', {
        collectionAddress,
        boxId,
        index: blindBoxIndex,
      })

      if (res.data.success) await router.push(res.data.result)
      else throw new Error(res.data.result)
    } catch (error: any) {
      console.error(error)
      setSubmiting(false)
      toastError(t(error.message))
      if (error.message === 'G Cent not enough') {
        onPresentGoldChargeModal()
      }
    } finally {
      setBuying(false)
      refresh()
    }
  }

  const renderBody = () => (
    <CardBody p="8px">
      <div ref={boxRef}>
        <ImageCard className="box-pic">
          {box?.image?.original && <NextImage src={box?.image?.original} height={500} width={500} />}
        </ImageCard>
        {submiting && (
          <AutoRenewIcon
            style={{
              position: 'absolute',
              top: 0,
              left: '10%',
              width: '80%',
              height: '80%',
            }}
            spin
            color="currentColor"
          />
        )}
      </div>
      <Flex justifyContent="space-around" flexDirection="row">
        <Button className="box-btn" mt={12} width="100%" variant="round" scale="sm" disabled={mint} isLoading={buying}>
          {BLINDBOX_PRICE} G
        </Button>
        {children}
      </Flex>
    </CardBody>
  )

  return (
    <BlindContainer disabled={mint}>
      <Text className="title">{blindBoxIndex}</Text>
      <StyledHotCollectionCard disabled={mint} background="transparent" onClick={presentDialog}>
        {renderBody()}
      </StyledHotCollectionCard>
    </BlindContainer>
  )
}

export default BlindBoxCard
